/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { addScormResponseAction } from 'actions/addScormResponseAction';
import { updateScoreAction } from 'actions/updateScoreAction';
import EditableBtn from 'components/lib/EditableBtn';
import Feedback from 'components/lib/Feedback';
import { SCORM } from 'pipwerks-scorm-api-wrapper';
import { Fragment as _, useContext, useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import getBlocksByType from 'services/apiHelpers/getBlocksByType';
import renderBlock from 'services/apiHelpers/renderBlock';
import { useScoringQuizzes } from 'services/apiHelpers/useScoringQuizzes';
import { findByKeyVal_V2 } from 'services/findByKeyVal';
import { getScore, setScore } from 'services/scoring';
import { unlockInteraction } from 'services/unlockInteraction';
import { Report, State } from 'store/store';
import { colors, getButtonFocus } from 'styles/variables';
import { interactionContext } from './interactionContext';
import Option from './Option';
// bump circleCI

const { lPink, lGrey2 } = colors;
const sessionLock = {};
const attempts = {};

function shuffle(array) {
  let m = array.length,
    t,
    i;

  let n = [...array];

  // While there remain elements to shuffle…

  while (m) {
    // Pick a remaining element…

    const i = Math.floor(Math.random() * m--);

    // And swap it with the current element.

    t = n[m];

    n[m] = n[i];

    n[i] = t;
  }

  i = Math.floor(Math.random() * n.length);

  if (n.every((option, k) => option.id === array[k].id)) {
    let j = Math.floor(Math.random() * n.length);

    while (j === i) {
      j = Math.floor(Math.random() * n.length);
    }

    t = n[i];

    n[i] = n[j];

    n[j] = t;
  }

  return n;
}

const Sortation = props => {
  const context = useContext(interactionContext);
  const {
    baseColor = lGrey2,
    // numberOfAttempts,
    score,
    showCorrectAnswer,
    showAttemptNumber,
    useMediaInFeedback = true,
    useFeedback = true,
    navigationBehaviour = {
      correct: false,
      incorrect: false,
    },
  } = context.props;

  const numberOfAttempts = context.hasAttemptFeedback
    ? context.props.numberOfAttempts
    : 0;

  const { id, children } = props;
  const answers = getBlocksByType('Answer', 'id');
  const heading0 = getBlocksByType('SortationHeading0', 'id');
  const heading1 = getBlocksByType('SortationHeading1', 'id');
  const options = children
    .filter(c => answers.includes(c.props.id))
    .map((c, i) => {
      const { id, children } = c.props;
      return { id, children };
    });
  const sortFrom = children.find(c => heading0.includes(c.props.id));
  const sortTo = children.find(c => heading1.includes(c.props.id));

  const [order, setOrder] = useState(() =>
    !window.dev() ? shuffle(options) : options
  );

  const [isLive, setIsLive] = useState(false);
  const [submit, setSubmit] = useState(sessionLock[context.id] ? 2 : 0);

  const scoringQuizzes = useScoringQuizzes();
  const { scoreAll } = useSelector((state: State) => {
    return Object.values(state.blocks).find(b => b.type === 'Module')
      ?.props as any;
  });

  const sortation = useRef<HTMLDivElement>(null);
  const hasAttempt = useRef<Boolean>(false);

  if (!attempts[context.id]) {
    attempts[context.id] = 0;
  }

  const isCorrect = useRef(false);

  sessionLock[context.id] =
    attempts[context.id] === numberOfAttempts &&
    score &&
    getScore(context.id) !== undefined;

  const [feedback, setFeedback] = useState(false);

  const dispatch = useDispatch();

  const updateOrder = $ => {
    const { source, destination } = $;

    const reorder = (list, startIndex, endIndex) => {
      const reordered = [...list];
      const [removed] = reordered.splice(startIndex, 1);
      reordered.splice(endIndex, 0, removed);

      return reordered;
    };

    const newOrder = reorder(order, source.index, destination.index);

    isCorrect.current = options.every((o, i) => o.id === newOrder[i].id);

    setScore(context.id, isCorrect.current);
    setOrder(newOrder);
  };

  const showFeedback = correct => {
    useFeedback && setFeedback(true);

    if (window.dev()) {
      return;
    }

    const sortationResolved =
      correct || attempts[context.id] === numberOfAttempts;

    if (sortationResolved && showCorrectAnswer === 1) {
      setOrder(options);
    }

    if (sortationResolved && score) {
      const alphabetized = Array.from(
        sortation.current!.querySelectorAll('.sortation-option')
      ).map((option: any) => {
        const id = option.id.replace('sortation-option-', '');
        return 'abcdefghijklmnopqrstuvwxyz'.split('')[
          options.findIndex((o: any) => o.id === id)
        ];
      });

      // dispatch if there's no scoringQuizzes etc
      let shouldDispatch = true;
      if (scoringQuizzes.length) {
        shouldDispatch = false;
      }

      let questionText:
        | NodeList
        | string[]
        | string = document.querySelectorAll(
        `#i${context.id} [class*="SortationHeading"]`
      ) as NodeList;

      if (questionText) {
        questionText = Array.from(questionText).map(q => {
          return (q as HTMLElement).innerText;
        });

        questionText = `Sort items from ${questionText[0]} to ${questionText[1]}`;
      }

      const getCorrectAndResponseOptions = entry => {
        let optionText: HTMLElement | string = document.querySelector(
          `#sortation-option-${entry.id}`
        ) as HTMLElement;
        if (optionText) {
          optionText = optionText.innerText;
        }

        return optionText;
      };

      const alphabetToArray = 'abcdefghijklmnopqrstuvwxyz'.split('');

      const correctExpanded = alphabetToArray
        .slice(0, options.length)
        .join(SCORM.version == '1.2' ? ',' : '[,]');

      const optionsIds = options.map(o => o.id || o.props.id);
      const orderIds = order.map(o => o.id || o.props.id);

      const responseExpanded = orderIds
        .map(id => {
          return alphabetToArray[optionsIds.indexOf(id)];
        })
        .join(SCORM.version == '1.2' ? ',' : '[,]');

      const action = {
        id: context.id,
        type: 'sequencing',
        question: questionText,
        reportable: 'sortation',
        response: responseExpanded,
        correct: correctExpanded,
        // @sam. updated this from the bool it was. to send a 1 | 0
        result: isCorrect.current ? 1 : 0,
      } as Report;

      // console.log(action);

      if (shouldDispatch) {
        dispatch(updateScoreAction(context.id, correct));
        dispatch(addScormResponseAction(action));
      }
    }

    if (isCorrect.current || attempts[context.id] === numberOfAttempts) {
      setSubmit(2);
      unlockInteraction(context.id);
    }
  };

  const onClose = () => {
    setFeedback(false);
  };

  useEffect(() => {
    if (hasAttempt.current && !isLive) {
      setIsLive(true);
    }

    if (parent.showFeedback) {
      parent.showFeedback[context.id] = correct => {
        if (feedback) {
          setFeedback(false);
          requestAnimationFrame(() =>
            showFeedback((isCorrect.current = correct))
          );
          //
        } else {
          showFeedback((isCorrect.current = correct));
        }
      };
    }

    if (order.length !== options.length) {
      setOrder(options);
    }
  });

  const SubmitBtn: any = renderBlock(
    findByKeyVal_V2(props.id, 'type', 'EditableButton')!,
    {
      EditableButton: ({ ...props }) => (
        <EditableBtn
          {...{ enabled: !!isLive, ...props }}
          onClick={() => {
            attempts[context.id] += 1;
            showFeedback(isCorrect.current);
          }}
        />
      ),
    }
  );

  useEffect(() => {
    if (window.dev() || !sortation.current) {
      return;
    }

    const alphabetized = Array.from(
      sortation.current.querySelectorAll('.sortation-option')
    ).map((option: any) => {
      const id = option.id.replace('sortation-option-', '');
      return 'abcdefghijklmnopqrstuvwxyz'.split('')[
        options.findIndex((o: any) => o.id === id)
      ];
    });
  });

  let parsedIsCorrect;

  if (attempts[context.id] < numberOfAttempts) {
    parsedIsCorrect = isCorrect.current || 'attempt';
  } else {
    parsedIsCorrect = isCorrect.current;
  }

  return (
    <_>
      <div
        ref={sortation}
        css={css`
          position: relative;
          width: 100%;
          padding: 10px 10px;
          border-radius: 10px;
          background: ${baseColor};
          pointer-events: ${submit === 0 ? 'auto' : 'none'};
        `}
      >
        {sortFrom}
        <DragDropContext
          onDragEnd={data => {
            if (!data.destination) {
              return;
            }

            updateOrder(data);
          }}
        >
          <Droppable droppableId={`Sortation_${id}`} type="droppableSortation">
            {provided => (
              <div ref={provided.innerRef}>
                {order.map((option, i) => (
                  <Draggable key={option.id} draggableId={option.id} index={i}>
                    {(provided, snapshot) => {
                      if (!hasAttempt.current && snapshot.isDragging)
                        hasAttempt.current = true;
                      return (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          css={css`
                            border-radius: 5px;
                            ${!snapshot.isDragging &&
                              `&:focus {
                              //
                              ${getButtonFocus(
                                context.props.panelBackground || lPink
                              )}
                            }`}
                          `}
                          style={{
                            ...provided.draggableProps.style,
                          }}
                        >
                          <Option
                            key={option.id}
                            option={option}
                            isDragging={snapshot.isDragging}
                          />
                        </div>
                      );
                    }}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {sortTo}
      </div>

      <div
        css={css`
          margin-top: 20px;
          ${!window.dev() &&
            `
            opacity: ${isLive && submit === 0 ? 1 : 0};
            pointer-events: ${isLive && submit === 0 ? 'auto' : 'none'};
            transition: opacity 0.2s;
          `}
        `}
      >
        {SubmitBtn}
      </div>

      {feedback && (
        <Feedback
          id={context.id}
          correct={window.dev() ? isCorrect.current : parsedIsCorrect}
          attempt={attempts[context.id]}
          numberOfAttempts={numberOfAttempts}
          showAttemptNumber={showAttemptNumber}
          showCorrectAnswer={showCorrectAnswer}
          useMediaInFeedback={useMediaInFeedback}
          navigationBehaviour={navigationBehaviour}
          onClose={() => {
            const tick = setInterval(() => {
              const submitButtonElement: any = document.querySelector(
                `#i${SubmitBtn?.props?.id}`
              );

              if (submitButtonElement) {
                submitButtonElement.focus();

                clearInterval(tick);
              }
            }, 0);

            onClose();
          }}
        />
      )}
    </_>
  );
};

export default Sortation;
